import { saveBlob } from "./api";

// Filled-in sample CSVs with one question of every type, generated in the browser.
// Admins download one, replace the rows with their own questions and import it.
// Unlike the server templates (blank, or one row per type), they show every type side by side.
// Every row has marks: sections have no default marks, so a blank `marks` cell is an import error.

// Section settings, written once per section on its first row (sections sample only)
const SECTION_COLUMNS = ["section", "section_duration_minutes", "section_total_questions", "section_description"];

const COLUMNS = [
  "question_type",
  "topic_name",
  "topic_name_hi",
  "marks",
  "negative_marks",
  "paragraph_group",
  "paragraph_en",
  "paragraph_hi",
  "question_en",
  "question_hi",
  // "image", // image uploads (images ZIP) are disabled for now
  "option_a_en",
  "option_b_en",
  "option_c_en",
  "option_d_en",
  "option_e_en",
  "option_f_en",
  "option_a_hi",
  "option_b_hi",
  "option_c_hi",
  "option_d_hi",
  "option_p_en",
  "option_q_en",
  "option_r_en",
  "option_s_en",
  "correct_answer",
  "numerical_value",
  "explanation_en",
  "explanation_hi",
];

// Rows in the same group stay together (and in one section for series samples).
// `subject` is the group's section in the sections sample.
const SAMPLE_GROUPS = [
  {
    subject: "Mathematics",
    rows: [
      {
        question_type: "SINGLE_CORRECT",
        topic_name: "Calculus",
        topic_name_hi: "कलन",
        marks: "4",
        negative_marks: "1",
        question_en: "Evaluate \\( \\int_0^1 x\\,dx \\).",
        question_hi: "\\( \\int_0^1 x\\,dx \\) का मान ज्ञात कीजिए।",
        option_a_en: "\\(0\\)",
        option_b_en: "\\( \\frac{1}{2} \\)",
        option_c_en: "\\(1\\)",
        option_d_en: "\\(2\\)",
        option_a_hi: "\\(0\\)",
        option_b_hi: "\\( \\frac{1}{2} \\)",
        option_c_hi: "\\(1\\)",
        option_d_hi: "\\(2\\)",
        correct_answer: "B",
        explanation_en: "\\( \\left[\\frac{x^2}{2}\\right]_0^1 = \\frac{1}{2} \\)",
        explanation_hi: "\\( \\left[\\frac{x^2}{2}\\right]_0^1 = \\frac{1}{2} \\) होगा।",
      },
    ],
  },
  {
    subject: "Physics",
    rows: [
      {
        question_type: "SINGLE_CORRECT",
        topic_name: "Kinematics",
        topic_name_hi: "शुद्धगतिकी",
        marks: "4",
        negative_marks: "1",
        question_en: "A body starts from rest with \\(a = 2\\,m/s^2\\). Its speed after 2 s is:",
        option_a_en: "\\(2\\,m/s\\)",
        option_b_en: "\\(4\\,m/s\\)",
        option_c_en: "\\(6\\,m/s\\)",
        option_d_en: "\\(8\\,m/s\\)",
        correct_answer: "B",
        explanation_en: "\\(v = at = 2 \\times 2 = 4\\,m/s\\)",
        explanation_hi: "\\(v = at = 2 \\times 2 = 4\\,m/s\\) होगी।",
      },
    ],
  },
  {
    subject: "Mathematics",
    rows: [
      {
        question_type: "MULTI_CORRECT",
        topic_name: "Number system",
        topic_name_hi: "संख्या पद्धति",
        marks: "4",
        negative_marks: "2",
        // Four options, as in JEE Advanced: partial marking gives +1 per correct option chosen (4 marks ÷ 4 options)
        question_en: "Which of these are prime?",
        option_a_en: "2",
        option_b_en: "3",
        option_c_en: "5",
        option_d_en: "9",
        correct_answer: "A,B,C",
      },
    ],
  },
  {
    subject: "Physics",
    rows: [
      {
        question_type: "NUMERICAL",
        topic_name: "Gravitation",
        topic_name_hi: "गुरुत्वाकर्षण",
        marks: "4",
        // negative_marks left blank: no negative marking
        question_en: "Acceleration due to gravity in \\(m/s^2\\) (one decimal place)",
        numerical_value: "9.8",
      },
    ],
  },
  {
    subject: "Chemistry",
    rows: [
      {
        question_type: "RANGE",
        topic_name: "Acids and bases",
        topic_name_hi: "अम्ल और क्षार",
        marks: "4",
        question_en: "The pH of drinking water lies in the range:",
        correct_answer: "6.5-8.5",
      },
    ],
  },
  {
    subject: "Chemistry",
    rows: [
      {
        question_type: "MATCH",
        topic_name: "Chemical bonding",
        topic_name_hi: "रासायनिक आबंधन",
        marks: "4",
        negative_marks: "1",
        question_en: "Match the compounds with their type",
        option_a_en: "\\ce{NaCl}",
        option_b_en: "\\ce{CH4}",
        option_c_en: "\\ce{H2SO4}",
        option_d_en: "\\ce{NaOH}",
        option_p_en: "Acid",
        option_q_en: "Ionic salt",
        option_r_en: "Base",
        option_s_en: "Covalent",
        correct_answer: "A-Q,B-S,C-P,D-R",
      },
    ],
  },
  {
    subject: "Physics",
    rows: [
      {
        question_type: "PARAGRAPH",
        topic_name: "Motion",
        topic_name_hi: "गति",
        marks: "3",
        negative_marks: "1",
        paragraph_group: "P1",
        paragraph_en: "A particle moves along the x-axis such that \\(x = t^3 - 6t^2 + 9t\\). Answer the following:",
        paragraph_hi: "एक कण x-अक्ष के अनुदिश इस प्रकार गति करता है कि \\(x = t^3 - 6t^2 + 9t\\)। निम्नलिखित के उत्तर दीजिए:",
        question_en: "Velocity at t = 0 is",
        option_a_en: "0",
        option_b_en: "3",
        option_c_en: "9",
        option_d_en: "-9",
        correct_answer: "C",
      },
      {
        question_type: "PARAGRAPH",
        topic_name: "Motion",
        topic_name_hi: "गति",
        marks: "3",
        negative_marks: "1",
        paragraph_group: "P1",
        question_en: "The particle is at rest at t =",
        option_a_en: "1 and 3",
        option_b_en: "only 1",
        option_c_en: "only 3",
        option_d_en: "2",
        correct_answer: "A",
      },
      {
        question_type: "PARAGRAPH",
        topic_name: "Motion",
        topic_name_hi: "गति",
        marks: "3",
        negative_marks: "1",
        paragraph_group: "P1",
        question_en: "Acceleration at t = 2 is",
        option_a_en: "0",
        option_b_en: "6",
        option_c_en: "-6",
        option_d_en: "12",
        correct_answer: "A",
      },
    ],
  },
];

// Settings for the sections sample, in the order the sections are created.
// Chemistry leaves the target blank to show that it defaults to its number of rows in the file.
const SAMPLE_SECTIONS = [
  {
    name: "Physics",
    section_duration_minutes: "60",
    section_total_questions: "30",
    section_description:
      "Each question carries the marks shown, and wrong answers cost the negative marks.\n\nUse a pen and paper for rough work.",
  },
  { name: "Chemistry", section_duration_minutes: "60" },
  {
    name: "Mathematics",
    section_duration_minutes: "90",
    section_total_questions: "30",
    section_description: "Multiple-correct questions have partial marking. One or more options are correct. +4 if you choose all the correct options and nothing else. If you choose only some of the correct options and no wrong one, +1 for each one. -2 if you choose any wrong option.",
  },
];

// RFC 4180: quote cells containing commas, quotes, line breaks or edge spaces
const csvCell = (value) => {
  const text = value == null ? "" : String(value);
  return /[",\r\n]|^\s|\s$/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const toCsv = (columns, rows) => {
  const lines = [columns, ...rows.map((row) => columns.map((col) => row[col]))].map((cells) =>
    cells.map(csvCell).join(",")
  );
  // The BOM makes Excel open the file as UTF-8, so Hindi text stays readable
  return `\uFEFF${lines.join("\r\n")}\r\n`;
};

const saveCsv = (csv, name) => saveBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), name);

export const buildSampleCsv = (sectionNames = []) => {
  const series = sectionNames.length > 0;
  const columns = series ? ["section", ...COLUMNS] : COLUMNS;
  const rows = SAMPLE_GROUPS.flatMap((group, index) =>
    group.rows.map((row) => (series ? { section: sectionNames[index % sectionNames.length], ...row } : row))
  );
  return toCsv(columns, rows);
};

// A whole test in one file: section columns first, then each section's questions.
// The section settings are filled only on the first row of each section.
// sectionalTiming false: durations are left blank, since a test with one timer would only warn "saved but not used"
export const buildSectionsSampleCsv = ({ sectionalTiming = true } = {}) => {
  const rows = SAMPLE_SECTIONS.flatMap(({ name, section_duration_minutes, ...settings }) =>
    SAMPLE_GROUPS.filter((group) => group.subject === name)
      .flatMap((group) => group.rows)
      .map((row, i) => ({
        section: name,
        ...(i === 0 ? { ...settings, ...(sectionalTiming && { section_duration_minutes }) } : {}),
        ...row,
      }))
  );
  return toCsv([...SECTION_COLUMNS, ...COLUMNS], rows);
};

// sectionNames: pass the series' section names for a series import (adds the `section` column)
export const downloadSampleCsv = (sectionNames) => {
  const series = Array.isArray(sectionNames);
  const names = series && sectionNames.length ? sectionNames : series ? ["Physics"] : [];
  saveCsv(buildSampleCsv(names), series ? "questions_sample_series.csv" : "questions_sample.csv");
};

// Creates Physics, Chemistry and Mathematics with their questions when imported with "Create missing sections"
export const downloadSectionsSampleCsv = (sectionalTiming = true) =>
  saveCsv(buildSectionsSampleCsv({ sectionalTiming }), "sections_questions_sample.csv");

// Subject-wise tests: each subject holds its own sections (Section A, Section B…), and every row names both.
// subject_description is written once per subject, the section settings once per section.
const SUBJECT_COLUMNS = ["subject", "subject_description"];

const SAMPLE_SUBJECTS = [
  {
    name: "Physics",
    subject_description: "Physics has two sections. Section A: single correct. Section B: numerical answers.",
    sections: [
      {
        name: "Section A",
        section_duration_minutes: "30",
        section_total_questions: "20",
        section_description: "Only one option is correct. +4 for a right answer, -1 for a wrong one.",
        // Kinematics (single) and the passage group
        pick: (group) => group.subject === "Physics" && group.rows[0].question_type !== "NUMERICAL",
      },
      {
        name: "Section B",
        section_duration_minutes: "20",
        section_total_questions: "5",
        section_description: "Type the numerical answer. No negative marking.",
        pick: (group) => group.subject === "Physics" && group.rows[0].question_type === "NUMERICAL",
      },
    ],
  },
  {
    name: "Chemistry",
    subject_description: "Chemistry has two sections.",
    sections: [
      {
        name: "Section A",
        section_duration_minutes: "30",
        section_total_questions: "20",
        pick: (group) => group.subject === "Chemistry" && group.rows[0].question_type === "MATCH",
      },
      {
        name: "Section B",
        section_duration_minutes: "20",
        pick: (group) => group.subject === "Chemistry" && group.rows[0].question_type === "RANGE",
      },
    ],
  },
  {
    name: "Mathematics",
    sections: [
      {
        name: "Section A",
        section_duration_minutes: "30",
        section_total_questions: "20",
        pick: (group) => group.subject === "Mathematics" && group.rows[0].question_type === "SINGLE_CORRECT",
      },
      {
        name: "Section B",
        section_duration_minutes: "20",
        section_description: "One or more options are correct. +4 if you choose all the correct options and nothing else. If you choose only some of the correct options and no wrong one, +1 for each one. -2 if you choose any wrong option.",
        pick: (group) => group.subject === "Mathematics" && group.rows[0].question_type === "MULTI_CORRECT",
      },
    ],
  },
];

export const buildSubjectsSampleCsv = ({ sectionalTiming = true } = {}) => {
  const rows = SAMPLE_SUBJECTS.flatMap((subject) => {
    let subjectWritten = false;
    return subject.sections.flatMap(({ name, pick, section_duration_minutes, ...settings }) =>
      SAMPLE_GROUPS.filter(pick)
        .flatMap((group) => group.rows)
        .map((row, i) => {
          const first = i === 0;
          const subjectSettings = first && !subjectWritten ? { subject_description: subject.subject_description } : {};
          if (first) subjectWritten = true;
          return {
            subject: subject.name,
            section: name,
            ...subjectSettings,
            ...(first ? { ...settings, ...(sectionalTiming && { section_duration_minutes }) } : {}),
            ...row,
          };
        })
    );
  });
  return toCsv([...SUBJECT_COLUMNS, ...SECTION_COLUMNS, ...COLUMNS], rows);
};

// Creates Physics, Chemistry and Mathematics, each with Section A and Section B, when imported with
// "Create missing sections" into a test that has "Subjects with sections" turned on
export const downloadSubjectsSampleCsv = (sectionalTiming = true) =>
  saveCsv(buildSubjectsSampleCsv({ sectionalTiming }), "subjects_sections_questions_sample.csv");
